import React, { useState, useEffect, useCallback } from 'react';
import { Settings, Plus, Edit2, Trash2, X, Calendar, Wrench, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { value: 'chauffage', label: 'Chauffage / Climatisation', icon: '🔥' },
  { value: 'ascenseur', label: 'Ascenseur / Monte-charge', icon: '🛗' },
  { value: 'plomberie', label: 'Plomberie / Eau', icon: '🚿' },
  { value: 'electricite', label: 'Électricité', icon: '⚡' },
  { value: 'securite', label: 'Sécurité / Incendie', icon: '🔒' },
  { value: 'ventilation', label: 'Ventilation / VMC', icon: '💨' },
  { value: 'portail', label: 'Portail / Accès', icon: '🚪' },
  { value: 'espaces_verts', label: 'Espaces verts', icon: '🌳' },
  { value: 'colonnes', label: 'Colonnes montantes', icon: '📊' },
  { value: 'toiture', label: 'Toiture / Étanchéité', icon: '🏠' },
  { value: 'autre', label: 'Autre', icon: '📦' },
];

const frequences = [
  { value: 'mensuel', label: 'Mensuel' },
  { value: 'trimestriel', label: 'Trimestriel' },
  { value: 'semestriel', label: 'Semestriel' },
  { value: 'annuel', label: 'Annuel' },
  { value: 'bisannuel', label: 'Tous les 2 ans' },
  { value: 'quinquennal', label: 'Tous les 5 ans' },
  { value: 'ponctuel', label: 'Sur demande / Ponctuel' },
];

export default function EquipementsTab({ buildingId, token }) {
  const [equipements, setEquipements] = useState([]);
  const [contrats, setContrats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const [filterCategorie, setFilterCategorie] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [eqRes, ctRes] = await Promise.all([
        fetch(`${API_URL}/api/carnet-entretien/equipements/${buildingId}`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/carnet-entretien/contrats/${buildingId}`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (eqRes.ok) setEquipements(await eqRes.json());
      if (ctRes.ok) setContrats(await ctRes.json());
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  }, [buildingId, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = editingId ? `${API_URL}/api/carnet-entretien/equipements/${editingId}` : `${API_URL}/api/carnet-entretien/equipements`;
    const method = editingId ? 'PUT' : 'POST';
    
    try {
      const response = await fetch(url, {
        method, headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, building_id: buildingId })
      });
      if (response.ok) {
        toast.success(editingId ? 'Équipement modifié' : 'Équipement ajouté');
        setShowModal(false); setEditingId(null); setForm({});
        fetchData();
      }
    } catch (error) { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet équipement ?')) return;
    try {
      await fetch(`${API_URL}/api/carnet-entretien/equipements/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      toast.success('Équipement supprimé');
      fetchData();
    } catch (error) { toast.error('Erreur'); }
  };

  const openEdit = (eq) => { setForm(eq); setEditingId(eq.id); setShowModal(true); };
  const openNew = () => { setForm({ categorie: 'autre' }); setEditingId(null); setShowModal(true); };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';
  
  const isMaintenanceDue = (eq) => {
    if (!eq.prochaine_maintenance) return false;
    const next = new Date(eq.prochaine_maintenance);
    const today = new Date();
    return next <= today;
  };

  const isMaintenanceSoon = (eq) => {
    if (!eq.prochaine_maintenance) return false;
    const next = new Date(eq.prochaine_maintenance);
    const today = new Date();
    const diff = (next - today) / (1000 * 60 * 60 * 24);
    return diff > 0 && diff <= 30;
  };

  const filtered = filterCategorie 
    ? equipements.filter(e => e.categorie === filterCategorie)
    : equipements;

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00D3A8]"></div></div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold">Équipements collectifs ({equipements.length})</h2>
          <p className="text-sm text-gray-500">Liste des équipements de l'immeuble avec suivi de maintenance</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={filterCategorie} onChange={(e) => setFilterCategorie(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
            <option value="">Toutes catégories</option>
            {categories.map(c => <option key={c.value} value={c.value}>{c.icon} {c.label}</option>)}
          </select>
          <button onClick={openNew} className="flex items-center gap-2 px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00B892]" data-testid="add-equipement-btn">
            <Plus className="h-5 w-5" />Ajouter
          </button>
        </div>
      </div>

      {/* Alertes maintenance */}
      {equipements.some(isMaintenanceDue) && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            <span className="font-medium">Maintenance en retard sur {equipements.filter(isMaintenanceDue).length} équipement(s)</span>
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <Settings className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">Aucun équipement enregistré</p>
          <p className="text-sm text-gray-400 mt-1">Commencez par ajouter les équipements collectifs de l'immeuble</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Équipement</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Localisation</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prestataire</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fréquence</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dernière intervention</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prochaine</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(eq => {
                const cat = categories.find(c => c.value === eq.categorie) || categories[categories.length-1];
                const due = isMaintenanceDue(eq);
                const soon = isMaintenanceSoon(eq);
                return (
                  <tr key={eq.id} className={`hover:bg-gray-50 ${due ? 'bg-red-50' : soon ? 'bg-orange-50' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{cat.icon}</span>
                        <div>
                          <p className="font-medium text-gray-900">{eq.nom}</p>
                          <p className="text-xs text-gray-500">{cat.label}</p>
                          {(eq.marque || eq.modele) && <p className="text-xs text-gray-400">{[eq.marque, eq.modele].filter(Boolean).join(' - ')}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{eq.localisation || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{eq.prestataire || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {frequences.find(f => f.value === eq.frequence_entretien)?.label || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDate(eq.derniere_maintenance)}</td>
                    <td className="px-4 py-3">
                      {eq.prochaine_maintenance ? (
                        <span className={`text-sm font-medium ${due ? 'text-red-600' : soon ? 'text-orange-600' : 'text-gray-600'}`}>
                          {formatDate(eq.prochaine_maintenance)}
                          {due && <span className="ml-1 text-xs">(en retard)</span>}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => openEdit(eq)} className="p-1 hover:bg-gray-100 rounded"><Edit2 className="h-4 w-4 text-gray-400" /></button>
                      <button onClick={() => handleDelete(eq.id)} className="p-1 hover:bg-red-50 rounded"><Trash2 className="h-4 w-4 text-red-400" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white">
              <h3 className="font-semibold">{editingId ? 'Modifier' : 'Nouvel'} équipement</h3>
              <button onClick={() => setShowModal(false)}><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1">Désignation *</label>
                  <input type="text" value={form.nom || ''} onChange={(e) => setForm({...form, nom: e.target.value})} required className="w-full px-3 py-2 border rounded-lg" placeholder="Ex: Chaudière collective gaz" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Catégorie</label>
                  <select value={form.categorie || 'autre'} onChange={(e) => setForm({...form, categorie: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                    {categories.map(c => <option key={c.value} value={c.value}>{c.icon} {c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Localisation</label>
                  <input type="text" value={form.localisation || ''} onChange={(e) => setForm({...form, localisation: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Ex: Sous-sol, Chaufferie" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Marque</label>
                  <input type="text" value={form.marque || ''} onChange={(e) => setForm({...form, marque: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Modèle</label>
                  <input type="text" value={form.modele || ''} onChange={(e) => setForm({...form, modele: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Numéro de série</label>
                  <input type="text" value={form.numero_serie || ''} onChange={(e) => setForm({...form, numero_serie: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date d'installation</label>
                  <input type="date" value={form.date_installation || ''} onChange={(e) => setForm({...form, date_installation: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date fin garantie</label>
                  <input type="date" value={form.date_fin_garantie || ''} onChange={(e) => setForm({...form, date_fin_garantie: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Durée de vie estimée (années)</label>
                  <input type="number" value={form.duree_vie_estimee || ''} onChange={(e) => setForm({...form, duree_vie_estimee: parseInt(e.target.value) || null})} className="w-full px-3 py-2 border rounded-lg" />
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Wrench className="h-4 w-4" />
                  Maintenance & Entretien
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Prestataire en charge</label>
                    <input type="text" value={form.prestataire || ''} onChange={(e) => setForm({...form, prestataire: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Nom de l'entreprise" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Contact prestataire</label>
                    <input type="text" value={form.prestataire_contact || ''} onChange={(e) => setForm({...form, prestataire_contact: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Tél ou email" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Fréquence d'entretien</label>
                    <select value={form.frequence_entretien || ''} onChange={(e) => setForm({...form, frequence_entretien: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                      <option value="">Sélectionner</option>
                      {frequences.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Contrat de maintenance lié</label>
                    <select value={form.contrat_maintenance_id || ''} onChange={(e) => setForm({...form, contrat_maintenance_id: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                      <option value="">Aucun contrat</option>
                      {contrats.filter(c => c.statut === 'actif').map(c => (
                        <option key={c.id} value={c.id}>{c.prestataire} - {c.type_contrat}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Dernière intervention</label>
                    <input type="date" value={form.derniere_maintenance || ''} onChange={(e) => setForm({...form, derniere_maintenance: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Prochaine échéance</label>
                    <input type="date" value={form.prochaine_maintenance || ''} onChange={(e) => setForm({...form, prochaine_maintenance: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Notes / Observations</label>
                <textarea value={form.notes || ''} onChange={(e) => setForm({...form, notes: e.target.value})} rows="2" className="w-full px-3 py-2 border rounded-lg" placeholder="Informations complémentaires..." />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
                <button type="submit" className="px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00B892]">{editingId ? 'Modifier' : 'Ajouter'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
